/**
 * 上传进度 / 取消 的通用逻辑
 *
 * 解决的问题：
 *   1. 大文件上传（几百 MB ~ 2GB）耗时很长，页面只有一个转圈动画，用户无法判断
 *      是"正在上传"还是"已经卡死"；
 *   2. 上传过程无法取消，一旦网络变差只能干等。
 *
 * 用法：
 *   import uploadProgressMixin from '@/utils/uploadProgressMixin'
 *   export default { mixins: [uploadProgressMixin], ... }
 *
 *   // 单文件
 *   this.beginUploadProgress(file.size)
 *   const handlers = this.createUploadHandlers()
 *   await uploadToQiniu(file, key, token, handlers)
 *
 *   // 多文件并发：总大小传各文件之和，每个文件分配一个 slot 下标
 *   this.beginUploadProgress(a.size + b.size)
 *   await Promise.all([
 *     uploadToQiniu(a, keyA, tokenA, this.createUploadHandlers(0)),
 *     uploadToQiniu(b, keyB, tokenB, this.createUploadHandlers(1))
 *   ])
 *
 *   finally { this.endUploadProgress() }
 */
export default {
  data () {
    return {
      isUploading: false,
      uploadPercent: 0,
      uploadLoadedBytes: 0,
      uploadTotalBytes: 0,
      uploadSpeedBps: 0,
      uploadAbortController: null
    }
  },

  computed: {
    uploadLoadedText () {
      return `${this.formatUploadSize(this.uploadLoadedBytes)} / ${this.formatUploadSize(this.uploadTotalBytes)}`
    },
    uploadSpeedText () {
      return this.uploadSpeedBps > 0 ? `${this.formatUploadSize(this.uploadSpeedBps)}/s` : '--'
    },
    uploadSlowTip () {
      if (!this.isUploading || this.uploadSpeedBps <= 0) return ''
      if (this.uploadSpeedBps < 1024 * 1024) {
        return '当前上行带宽较低（< 1MB/s），大文件上传会比较慢，请保持页面打开'
      }
      return ''
    }
  },

  methods: {
    /** 开始一次上传：totalBytes 为本次要上传的总字节数（多文件时传总和） */
    beginUploadProgress (totalBytes) {
      this.isUploading = true
      this.uploadPercent = 0
      this.uploadLoadedBytes = 0
      this.uploadTotalBytes = Number(totalBytes) || 0
      this.uploadSpeedBps = 0
      this.uploadAbortController = new AbortController()
      this.$_uploadStartedAt = Date.now()
      this.$_uploadSlots = {}
    },

    /**
     * 生成传给 uploadToQiniu 的 { signal, onProgress }
     * @param {Number} slotIndex 多文件并发时用于区分不同文件，单文件可省略
     */
    createUploadHandlers (slotIndex = 0) {
      return {
        signal: this.uploadAbortController ? this.uploadAbortController.signal : undefined,
        onProgress: progress => this.updateUploadProgress(progress, slotIndex)
      }
    },

    updateUploadProgress (progress, slotIndex = 0) {
      if (!this.$_uploadSlots) this.$_uploadSlots = {}
      this.$_uploadSlots[slotIndex] = (progress && progress.loaded) || 0

      const slots = this.$_uploadSlots
      let loaded = 0
      for (const key of Object.keys(slots)) loaded += slots[key]

      const elapsed = Math.max((Date.now() - (this.$_uploadStartedAt || Date.now())) / 1000, 0.001)
      this.uploadLoadedBytes = loaded
      this.uploadSpeedBps = loaded / elapsed
      this.uploadPercent = this.uploadTotalBytes
        ? Math.min(100, Number(((loaded * 100) / this.uploadTotalBytes).toFixed(1)))
        : 0
    },

    endUploadProgress () {
      this.isUploading = false
      this.uploadAbortController = null
      this.$_uploadSlots = null
    },

    cancelUpload () {
      if (this.uploadAbortController) this.uploadAbortController.abort()
    },

    /** 判断错误是否由"用户取消上传"引起 */
    isUploadCanceledError (error) {
      return !!(
        error && (
          error.canceled === true ||
          error.code === 'ERR_CANCELED' ||
          error.name === 'CanceledError' ||
          error.name === 'AbortError'
        )
      )
    },

    formatUploadSize (bytes) {
      const size = Number(bytes) || 0
      if (size < 1024) return `${size.toFixed(0)} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`
      return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`
    }
  }
}
